import React, { useState } from "react";
import Hooks from "@hooks";
import { Form } from "@components";
import { Application, LandingZoneRole, Role } from "@store/types";
import { Access } from "../../../../../../context";

export type Handlers = {
  reset: () => void;
  submit: () => void;
};

type Props = {
  application: Application;
  onChange?: <Key extends keyof Access>(key: Key, value: Access[Key]) => void;
  onError?: <Key extends keyof Access>(key: Key, error: Error) => void;
  onSubmit: (values: Access) => void;
};

const Component: React.RefForwardingComponent<Handlers, Props> = (
  props,
  ref
) => {
  const form = Form.useManager<Partial<Access>>({
    environment: undefined,
    subCategoryName: undefined,
    role: undefined,
    infrastructureApplication: undefined,
    accessDetails: undefined,
  });
  const [environments, , environments_fetching] = Hooks.useEnvironments(
    props.application.manual ? undefined : props.application.id
  );
  const [roles, , roles_fetching] = Hooks.useRoles(
    props.application.id,
    form.values.environment?.name
  );
  const [
    infra_apps,
    ,
    infra_apps_fetching,
  ] = Hooks.useInfrastructureApplications(props.application.id);
  const [roleDesc, setRoleDesc] = useState("" || undefined);
  const landingZoneRoles = Hooks.useLandingZoneRoles();

  React.useImperativeHandle(ref, () => ({
    submit() {
      Promise.all(Object.entries(form.values).map(validate)).then(
        (validations) => {
          const valid = validations.reduce((valid, [name, error]) => {
            return valid && !error;
          }, true);

          if (valid) {
            props.onSubmit(form.values as Access); // [TODO] Check
            form.reset();
          } else {
            validations.forEach(([name, error]) => {
              form.set(Form.target.ERROR, [name, error]);
            });
          }
        }
      );
    },
    reset() {
      form.reset();
    },
  }));

  // Helpers
  function mapToRole(landingZoneRole: LandingZoneRole): Role {
    return {
      id: landingZoneRole.id,
      name: landingZoneRole.landingZoneRoleName,
      description: "",
    };
  }

  // Handlers

  function handleEnvironmentValueChange(
    name: "environment",
    value: string
  ): void {
    const environment = (environments || []).find(({ name }) => name === value);

    if (environment) {
      handleFormValueChange(name, environment);
    }
  }

  function handleRoleValueChange(name: "role", value: string): void {
    const roleDataByRoleName: any = roles?.find((x: any) => x.name === value);
    setRoleDesc(roleDataByRoleName?.description);
    if (props.application.manual) {
      handleFormValueChange(name, {
        id: Number.NaN,
        name: value,
      });
    } else {
      const role = (roles || []).find(({ name }) => name === value);

      if (role) {
        handleFormValueChange(name, role);
      }
    }
  }

  function handleLandingZoneRoleValueChange(name: "role", value: string): void {
    const landingZoneRole = (landingZoneRoles.value || []).find(
      ({ landingZoneRoleName }) => landingZoneRoleName === value
    );

    if (landingZoneRole) {
      handleFormValueChange(name, mapToRole(landingZoneRole));
    }
  }

  function handleInfraAppValueChange(
    name: "infrastructureApplication",
    value: string
  ): void {
    const infra_app = (infra_apps || []).find(({ name }) => name === value);

    if (infra_app) {
      handleFormValueChange(name, infra_app);
    }
  }

  function handleFormValueChange<Key extends keyof Access>(
    name: Key,
    value: Access[Key]
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
    if (props.onChange) {
      props.onChange(name, value);
    }
  }

  // Helpers

  function isEnvironmentDropdownDisabled(): boolean {
    return environments_fetching || !environments?.length;
  }

  const environmentDropdownOptions = React.useMemo(() => {
    return (environments || []).map(({ name }) => `${name}`);
  }, [environments]);

  function isRoleDropdownDisabled(): boolean {
    return roles_fetching || !roles?.length;
  }

  function isLZRoleDropdownDisabled(): boolean {
    return landingZoneRoles.fetching || !landingZoneRoles.value?.length;
  }

  const roleDropdownOptions = React.useMemo(() => {
    return (roles || [])
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map(({ name }) => `${name}`);
  }, [roles]);

  const landingZoneRoleDropdownOptions = React.useMemo(() => {
    const landgingZonesWithAllRoles: string[] = [
      "ACTIVE Network Canada act2",
      "ACTIVE Network Security actl",
      "ACTIVE Network Shared Services acts",
      "ACTIVE Network Test actt",
      "ACTIVE Network US",
      "Dev LZ 1",
      "Dev LZ 2",
      "Dev LZ 3",
      "Dev LZ 4",
      "ACTIVE Network WANHub actw",
    ];

    if (
      !landgingZonesWithAllRoles.includes(props.application.name) &&
      landingZoneRoles.value !== undefined &&
      landingZoneRoles.value !== null
    ) {
      landingZoneRoles.value = landingZoneRoles.value.filter(
        (item) =>
          !item.landingZoneRoleName.includes("LZ Admin") &&
          !item.landingZoneRoleName.includes("LZ Engineer") &&
          !item.landingZoneRoleName.includes("LZ Viewer")
      );
    }

    return (landingZoneRoles.value || [])
      .sort((a, b) =>
        a.landingZoneRoleName.localeCompare(b.landingZoneRoleName, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map(({ landingZoneRoleName }) => `${landingZoneRoleName}`);
  }, [landingZoneRoles.value]);

  function isInfraAppDropdownDisabled(): boolean {
    return infra_apps_fetching || !infra_apps?.length;
  }

  const infraAppDropdownOptions = React.useMemo(() => {
    return (infra_apps || []).map(({ name }) => `${name}`);
  }, [infra_apps]);

  function getFormValue(key: keyof Access): string {
    const value = form.values[key];

    return typeof value === "string" ? value : value?.name || "";
  }

  function validate([name, value]: [string, any]): Promise<
    [keyof Access, Error?]
  > {
    let error: Error | undefined;

    switch (name) {
      case "environment":
        if (!props.application.lz) {
          error = Form.Validators.hasValue(value);
        }
        break;
      case "accessDetails":
        var isStringWhitespaceOnly =
          value !== undefined && validateWhitespaceString(value.toString());
        error = Form.Validators.hasValue(value);

        if (isStringWhitespaceOnly) error = Form.Validators.hasValue(undefined);

        break;
      case "role":
        error = Form.Validators.hasValue(
          props.application?.manual ? value?.name : value
        );
        break;
      case "subCategoryName":
        error =
          props.application?.manual && infraAppDropdownOptions.length
            ? Form.Validators.hasValue(value)
            : undefined;
        break;
    }
    if (error && props.onError) {
      props.onError(name as keyof Access, error);
    }

    return Promise.resolve([name as keyof Access, error]);
  }

  function validateWhitespaceString(value: string) {
    var isWhitespaceOnly = true;
    var character;

    for (
      var stringIterator = 0;
      stringIterator < value.length;
      stringIterator++
    ) {
      character = value.charAt(stringIterator);

      if (character !== " ") {
        isWhitespaceOnly = false;
        break;
      }
    }

    return isWhitespaceOnly;
  }

  const roleDescData = roleDesc ? (
    <div style={{ width: "95%", marginLeft: "10px" }}>
      <span
        style={{
          fontStyle: "italic",
          fontFamily: "Arial",
          fontSize: "11px",
          overflowWrap: "break-word",
          color: "rgba(0, 0, 0, 0.54)",
        }}
      >
        {roleDesc}
      </span>
    </div>
  ) : null;

  return (
    <Form>
      {props.application.lz ? null : (
        <Form.Dropdown
          disabled={isEnvironmentDropdownDisabled()}
          error={form.errors.environment}
          label={{
            text: "Environment",
            tooltip:
              "This pertains to where the system will run. (Ex. PROD, DEV etc.)",
          }}
          loading={environments_fetching}
          name="environment"
          onChange={handleEnvironmentValueChange}
          options={environmentDropdownOptions}
          required
          size={12}
          value={getFormValue("environment")}
        />
      )}
      {props.application.manual ? (
        <Form.Textbox
          error={form.errors.subCategoryName}
          label="Device / Server / Database / Network Name"
          maxLength={200}
          name="subCategoryName"
          onChange={handleFormValueChange}
          required
          size={12}
          value={getFormValue("subCategoryName")}
        />
      ) : null}
      {props.application.manual ? (
        <Form.Textbox
          error={form.errors.role}
          label="Role (e.g. Admin, User, Read/Write etc.)"
          maxLength={200}
          name="role"
          onChange={handleRoleValueChange}
          required
          size={12}
          value={getFormValue("role")}
        />
      ) : props.application.lz ? (
        <>
          <Form.Dropdown
            disabled={isLZRoleDropdownDisabled()}
            error={form.errors.role}
            label={{
              text: "Role",
              tooltip: "Role for landing zone access.",
            }}
            loading={landingZoneRoles.fetching}
            name="role"
            onChange={handleLandingZoneRoleValueChange}
            options={landingZoneRoleDropdownOptions}
            required
            size={12}
            value={getFormValue("role")}
          />
        </>
      ) : (
        <>
          <Form.Dropdown
            disabled={isRoleDropdownDisabled()}
            error={form.errors.role}
            label={{
              text: "Role",
              tooltip:
                "It is a level of access (e.g. Admin, Supv, etc.) for the requested system.",
            }}
            loading={roles_fetching}
            name="role"
            onChange={handleRoleValueChange}
            options={roleDropdownOptions}
            required
            size={12}
            value={getFormValue("role")}
          />
        </>
      )}
      {props.application.manual && infraAppDropdownOptions.length ? (
        <Form.Dropdown
          disabled={isInfraAppDropdownDisabled()}
          error={form.errors.infrastructureApplication}
          label="Infrastructure Application"
          loading={infra_apps_fetching}
          name="infrastructureApplication"
          onChange={handleInfraAppValueChange}
          options={infraAppDropdownOptions}
          size={12}
          value={getFormValue("infrastructureApplication")}
        />
      ) : null}
      {roleDescData}
      <Form.Textarea
        error={form.errors.accessDetails}
        label={{
          text: "Access Details",
          caption: props.application.uamNote,
        }}
        maxLength={1000}
        name="accessDetails"
        onChange={handleFormValueChange}
        placeholder={props.application.accessDetailNote || "Write details here"}
        required
        rows={5}
        rowsMax={5}
        size={12}
        value={getFormValue("accessDetails")}
      />
    </Form>
  );
};

export const AccessForm = React.forwardRef(Component);
